import { readMiniAppLaunchParams } from './miniAppInboundLaunch';

/** Synthetic wallet prefix — never used for Hyperliquid browser fetches. */
export const DEMO_WALLET_PREFIX = '0xDemo';

export function isDemoQueryValue(raw: string | null | undefined): boolean {
  if (raw === null || raw === undefined) return false;
  const v = String(raw).trim().toLowerCase();
  if (v === '') return true;
  if (v === '0' || v === 'false' || v === 'no') return false;
  return v === '1' || v === 'true' || v === 'yes' || v === 'demo';
}

/** True when `/miniapp?demo=1` (or `?demo`, `?demo=true`) is active. */
export function isMiniAppDemoMode(): boolean {
  try {
    const params = readMiniAppLaunchParams();
    if (params.has('demo')) return isDemoQueryValue(params.get('demo'));
  } catch {}
  return false;
}

/** Persist demo flag on in-app navigation URLs. */
export function appendDemoQuery(params: URLSearchParams): void {
  if (isMiniAppDemoMode()) {
    params.set('demo', '1');
  }
}

export function isDemoWalletAddress(address: string | null | undefined): boolean {
  return String(address || '').trim().toLowerCase().startsWith(DEMO_WALLET_PREFIX.toLowerCase());
}

const DEMO_READ_REWRITES: Array<{ match: (path: string) => boolean; demo: string }> = [
  { match: (p) => p.endsWith('/user/status'), demo: '/api/miniapp/demo/user/status' },
  { match: (p) => p.endsWith('/portfolio/dashboard'), demo: '/api/miniapp/demo/portfolio/dashboard' },
  { match: (p) => p.endsWith('/portfolio/stats'), demo: '/api/miniapp/demo/portfolio/stats' },
  { match: (p) => p.endsWith('/portfolio/positions'), demo: '/api/miniapp/demo/portfolio/positions' },
  { match: (p) => p.endsWith('/positions'), demo: '/api/miniapp/demo/positions' },
  { match: (p) => p.endsWith('/portfolio/trades-analytics'), demo: '/api/miniapp/demo/portfolio/trades-analytics' },
  { match: (p) => p.endsWith('/portfolio/trades-pnl-card'), demo: '/api/miniapp/demo/portfolio/trades-pnl-card' },
  { match: (p) => p.endsWith('/portfolio/trades-export'), demo: '/api/miniapp/demo/portfolio/trades-export' },
  { match: (p) => p.endsWith('/portfolio/trades-pdf'), demo: '/api/miniapp/demo/portfolio/trades-pdf' },
  { match: (p) => p.endsWith('/portfolio/positions/strategy-hints'), demo: '/api/miniapp/demo/portfolio/positions/strategy-hints' },
  { match: (p) => p.endsWith('/automation/my-strategies'), demo: '/api/miniapp/demo/automation/my-strategies' },
  { match: (p) => p.endsWith('/automation/live-performance'), demo: '/api/miniapp/demo/automation/live-performance' },
  { match: (p) => p.endsWith('/automation/global-settings'), demo: '/api/miniapp/demo/automation/global-settings' },
  { match: (p) => p.endsWith('/automation/recent-signals'), demo: '/api/miniapp/demo/automation/recent-signals' },
  { match: (p) => p.endsWith('/automation/state'), demo: '/api/miniapp/demo/automation/state' },
  { match: (p) => p.endsWith('/rewards'), demo: '/api/miniapp/demo/rewards' },
  { match: (p) => p.endsWith('/guilds/list'), demo: '/api/miniapp/demo/guilds/list' },
  { match: (p) => p.endsWith('/mcp/settings'), demo: '/api/miniapp/demo/mcp/settings' },
  { match: (p) => p.endsWith('/mcp/conversations/messages/list'), demo: '/api/miniapp/demo/mcp/conversations/messages/list' },
  { match: (p) => p.endsWith('/mcp/conversations/sync'), demo: '/api/miniapp/demo/mcp/conversations/sync' },
  { match: (p) => p.endsWith('/mcp/chat'), demo: '/api/miniapp/demo/mcp/chat' },
  { match: (p) => p.endsWith('/mcp/chat/stream'), demo: '/api/miniapp/demo/mcp/chat/stream' },
  { match: (p) => p.endsWith('/mcp/warm'), demo: '/api/miniapp/demo/mcp/warm' },
  { match: (p) => p.endsWith('/mcp/agent-activity'), demo: '/api/miniapp/demo/mcp/agent-activity' },
  { match: (p) => p.endsWith('/notifications/poll'), demo: '/api/miniapp/demo/notifications/poll' },
];

function normalizeMiniappApiPath(endpoint: string): string {
  let path = String(endpoint || '').split('?')[0];
  path = path
    .replace('/api/miniapp/dev-status', '/api/miniapp/user/status')
    .replace('/api/miniapp/dev-rewards', '/api/miniapp/rewards')
    .replace('/api/miniapp/dev-portfolio-dashboard', '/api/miniapp/portfolio/dashboard')
    .replace('/api/miniapp/dev-trading-stats', '/api/miniapp/portfolio/stats')
    .replace('/api/miniapp/dev-portfolio-trades-analytics', '/api/miniapp/portfolio/trades-analytics')
    .replace('/api/miniapp/dev-portfolio-trades-pnl-card', '/api/miniapp/portfolio/trades-pnl-card')
    .replace('/api/miniapp/dev-portfolio-trades-export', '/api/miniapp/portfolio/trades-export')
    .replace('/api/miniapp/dev-portfolio-trades-pdf', '/api/miniapp/portfolio/trades-pdf')
    .replace('/api/miniapp/dev-automation/', '/api/miniapp/automation/')
    .replace('/api/miniapp/dev-portfolio/', '/api/miniapp/portfolio/')
    .replace('/api/miniapp/dev-guilds/', '/api/miniapp/guilds/')
    .replace('/api/miniapp/dev-mcp/', '/api/miniapp/mcp/')
    .replace('/api/miniapp/dev-notifications/', '/api/miniapp/notifications/');
  return path;
}

const DEMO_MUTATION_PATTERNS: RegExp[] = [
  /\/trade\/execute\b/i,
  /\/portfolio\/position\b/i,
  /\/portfolio\/positions\/close/i,
  /\/automation\/(my-strategies\/dispatch|share-basket|subscribe|remove-strategy|update-config|pair-detail)\b/i,
  /\/guilds\/(request-join|join|approve|leader)\b/i,
  /\/wallets?\b/i,
  /\/mcp\/(conversations\/(sync|delete|share)|providers\/(upsert|delete|test)|schedule\/run-now|ml\/promote-vote)\b/i,
  /\/hyperliquid\b/i,
  /\/fee-approval\b/i,
];

export function resolveDemoReadEndpoint(endpoint: string): string | null {
  if (!isMiniAppDemoMode()) return null;
  const path = normalizeMiniappApiPath(endpoint);
  for (const row of DEMO_READ_REWRITES) {
    if (row.match(path)) return row.demo;
  }
  return null;
}

export function isDemoMutationEndpoint(endpoint: string): boolean {
  if (!isMiniAppDemoMode()) return false;
  const path = normalizeMiniappApiPath(endpoint);
  if (path.includes('/demo/')) return false;
  return DEMO_MUTATION_PATTERNS.some((re) => re.test(path));
}

export const DEMO_MUTATION_ERROR = 'Demo mode — sign in to trade';
